import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { RefreshCcw, Layers } from "lucide-react";
import { api } from "@/lib/api";
import { inr, fmtDate, fmtDateTime } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { useApp } from "@/context/AppContext";

export function EarningPlan({ dpId, dp, onChanged }) {
  const { role } = useApp();
  const canManage = ["Super Admin", "Operations Manager"].includes(role);
  const [models, setModels] = useState([]);
  const [history, setHistory] = useState([]);
  const [current, setCurrent] = useState(null);
  const [open, setOpen] = useState(false);
  const [sel, setSel] = useState("");
  const [confirm, setConfirm] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    api.get("/earning-models/active").then(({ data }) => setModels(data)).catch(() => {});
    api.get(`/dp/${dpId}/earning-model-history`).then(({ data }) => setHistory(data)).catch(() => {});
    if (dp?.earning_model_id) {
      api.get(`/earning-models/${dp.earning_model_id}`).then(({ data }) => setCurrent(data)).catch(() => setCurrent(null));
    } else setCurrent(null);
  }, [dpId, dp?.earning_model_id]);
  useEffect(() => { load(); }, [load]);

  const doChange = async () => {
    setSaving(true);
    try {
      await api.put(`/dp/${dpId}/earning-model`, { model_id: sel });
      toast.success("Earning model changed");
      setConfirm(false); setOpen(false); setSel("");
      load(); onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } finally { setSaving(false); }
  };

  return (
    <div className="space-y-5" data-testid="earning-plan-section">
      <div className="rounded-xl border border-border bg-card p-5">
        <div className="mb-3 flex items-center justify-between">
          <h3 className="flex items-center gap-2 font-head text-sm font-semibold"><Layers className="h-4 w-4 text-primary" /> Earning Plan</h3>
          {canManage && <Button size="sm" variant="outline" onClick={() => setOpen(true)} data-testid="change-model-btn"><RefreshCcw className="mr-1.5 h-3.5 w-3.5" /> Change Earning Model</Button>}
        </div>
        {current ? (
          <div className="grid grid-cols-2 gap-3 text-sm sm:grid-cols-4">
            <KV k="Current Plan" v={current.name} strong />
            <KV k="Model Type" v={current.type} />
            <KV k="Rate" v={current.type === "Flat Rate" ? `${inr(current.rate)}/order` : current.type === "Order Slab" ? `${(current.slabs || []).length} slabs` : `Base ${inr(current.custom?.base_rate)}/order`} />
            <KV k="Status" v={<span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${current.active ? "bg-emerald-500/10 text-emerald-500" : "bg-secondary text-muted-foreground"}`} data-testid="plan-status">{current.active ? "Active" : "Inactive"}</span>} />
          </div>
        ) : (
          <p className="text-sm text-muted-foreground" data-testid="no-plan">No earning model assigned — this partner uses the global earning slabs from Settings.</p>
        )}
        {current && current.type === "Order Slab" && (
          <div className="mt-3 overflow-hidden rounded-lg border border-border">
            <Table>
              <TableHeader><TableRow className="hover:bg-transparent"><TableHead>Orders</TableHead><TableHead className="text-right">Rate</TableHead></TableRow></TableHeader>
              <TableBody>
                {(current.slabs || []).map((s, i) => <TableRow key={i}><TableCell>{s.min_orders} – {s.max_orders ?? "∞"}</TableCell><TableCell className="text-right tabular">{inr(s.rate)}</TableCell></TableRow>)}
              </TableBody>
            </Table>
          </div>
        )}
      </div>

      <div>
        <h3 className="mb-2 font-head text-sm font-semibold">Earning Model History</h3>
        <div className="overflow-hidden rounded-xl border border-border bg-card">
          <Table>
            <TableHeader><TableRow className="hover:bg-transparent"><TableHead>Date</TableHead><TableHead>Previous Model</TableHead><TableHead>New Model</TableHead><TableHead>Changed By</TableHead></TableRow></TableHeader>
            <TableBody>
              {history.length === 0 && <TableRow><TableCell colSpan={4} className="text-center text-muted-foreground">No plan changes</TableCell></TableRow>}
              {history.map((h) => (
                <TableRow key={h.id} data-testid={`model-history-${h.id}`}>
                  <TableCell>{fmtDateTime(h.date)}</TableCell>
                  <TableCell className="text-muted-foreground">{h.previous_model}</TableCell>
                  <TableCell className="font-medium text-primary">{h.new_model}</TableCell>
                  <TableCell className="text-xs text-muted-foreground">{h.changed_by}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>

      <Dialog open={open} onOpenChange={(o) => !o && setOpen(false)}>
        <DialogContent className="bg-card" data-testid="change-model-dialog">
          <DialogHeader><DialogTitle className="font-head">Change Earning Model</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">Current: <span className="font-semibold text-foreground">{current?.name || "Global slabs"}</span></p>
          <div>
            <Label>Change to</Label>
            <Select value={sel} onValueChange={setSel}>
              <SelectTrigger data-testid="change-model-select"><SelectValue placeholder="Select active model" /></SelectTrigger>
              <SelectContent>{models.filter((m) => m.id !== dp?.earning_model_id).map((m) => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={() => { if (!sel) { toast.error("Select a model"); return; } setConfirm(true); }} disabled={!sel} data-testid="change-model-next">Continue</Button>
          </DialogFooter>
          <AlertDialog open={confirm} onOpenChange={setConfirm}>
            <AlertDialogContent data-testid="confirm-change-model">
              <AlertDialogHeader><AlertDialogTitle>Confirm change?</AlertDialogTitle>
                <AlertDialogDescription>Are you sure you want to change the earning model for {dp?.full_name} to "{models.find((m) => m.id === sel)?.name}"? Historical earnings will NOT be recalculated — only future entries use the new model.</AlertDialogDescription></AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction onClick={doChange} disabled={saving} data-testid="final-change-model">{saving ? "Saving…" : "Confirm Change"}</AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const KV = ({ k, v, strong }) => (
  <div><p className="text-[11px] uppercase tracking-wider text-muted-foreground">{k}</p><p className={`${strong ? "font-head text-base font-bold text-primary" : "font-medium"}`}>{v}</p></div>
);
